import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronLeft,
  faCircleQuestion,
  faPlus,
  faTrashAlt,
  faShuffle,
} from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

const VocabularyTerms = ({
  questions,
  assignedStatus,
  questionCount,
  selectedQuestionIndex,
  pageQuestionIds,
  numPages,
  currentPage,
  startIndex,
  endIndex,
  isDragging,
  dropIndex,
  assignmentId,
  onGetQuestionDetails,
  onPrevPage,
  onNextPage,
  onDragStart,
  onDragOver,
  onDrop,
  onDragLeave,
  onDeleteClick,
  onShuffleClick,
  isPreview
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const navigate = useNavigate();
  const [truncatedIds, setTruncatedIds] = useState({});

  const canEdit = assignedStatus > 3;

  const measureTruncation = (questionIndex) => (el) => {
    if (!el) return;
    const label = el.querySelector('.vocab-tile-label');
    const isTruncated = !!label && label.scrollWidth > label.clientWidth;
    setTruncatedIds((prev) =>
      prev[questionIndex] === isTruncated ? prev : { ...prev, [questionIndex]: isTruncated }
    );
  };

  const vocabMap = questions?.vocabulary?.reduce((acc, vocab) => {
    acc[vocab.id] = vocab;
    return acc;
  }, {});

  return (
    <div className="justify-content-between">
      <div className="flashcard-content">
        <div className={`classroom-content-box ${isOpen ? 'open' : ''}`}>
          <div
            className="classroom-content-header justify-content-between align-item-center d-flex pb-0 cursor-pointer"
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <h6 className="align-content-center mb-0 no-select">Vocabulary Terms</h6>
            <button className="z-index10 grey-clr">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>

          {isOpen && (
            <div className="mt-2">
              {numPages > 1 && (
                <ul className="flex-row gap-2 justify-content-end pb-2 d-flex">
                  <li className="bg-body">
                    <button
                      onClick={onPrevPage}
                      onDragEnter={isDragging ? onPrevPage : undefined}
                      className={`btn btn-castleWhite px-3 ${currentPage === 0 ? 'disabled' : ''}`}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                  </li>
                  <li className="bg-body">
                    <button
                      onClick={onNextPage}
                      onDragEnter={isDragging ? onNextPage : undefined}
                      className={`btn btn-castleWhite px-3 ${currentPage === numPages - 1 ? 'disabled' : ''}`}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </li>
                </ul>
              )}
              <ul className="d-flex flex-wrap m-0 p-0 gap-3">
                {!questions?.vocabulary ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={i}>
                      <li className="question-list-item px-3">
                        <div className="w-140 h-40 skeleton placeholder-glow rounded"></div>
                      </li>
                    </div>
                  ))
                ) : (
                  pageQuestionIds?.map((questionId, index) => {
                    const vocab = vocabMap[questionId];
                    const vocabName = vocab?.name ?? '';
                    const questionIndex = startIndex + index;
                    const isSelected = selectedQuestionIndex === questionIndex;
                    const isDraggable = !isPreview && canEdit && (isDragging || isSelected);

                    return (
                      <>
                        <li
                          key={questionIndex}
                          onClick={() => onGetQuestionDetails(questionIndex)}
                          {...(isDraggable && {
                            draggable: true,
                            onDragStart: (e) => onDragStart(e, index, questionIndex),
                            onDragOver: (e) => onDragOver(e, index),
                            onDrop: (e) => onDrop(e, questionIndex + 1),
                            onDragLeave,
                          })}
                          className={`${isSelected ? 'active' : ''} ${!isPreview ? 'cursor-grab' : 'cursor-pointer'} question-list-item px-3`}
                        >
                          {dropIndex === index ? (
                            <div className="vocab-tile d-flex align-items-center gap-1" />
                          ) : truncatedIds[questionIndex] ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip className="custom-tooltip">
                                <span dangerouslySetInnerHTML={{ __html: vocabName }}>
                                </span></Tooltip>}
                            >
                              <div className="vocab-tile d-flex align-items-center gap-1" ref={measureTruncation(questionIndex)}>
                                <div className="vocab-tile-index">{questionIndex + 1}.</div>
                                <div className="vocab-tile-label" dangerouslySetInnerHTML={{ __html: vocabName }} />
                              </div>
                            </OverlayTrigger>
                          ) : (
                            <div className="vocab-tile d-flex align-items-center gap-1" ref={measureTruncation(questionIndex)}>
                              <div className="vocab-tile-index">{questionIndex + 1}.</div>
                              <div className="vocab-tile-label" dangerouslySetInnerHTML={{ __html: vocabName }} />
                            </div>
                          )}
                        </li>

                        {dropIndex === index &&
                        questionIndex + 1 !== endIndex &&
                        questionIndex + 1 !== questionCount && (
                          <li className="cursor-grab question-list-item px-3">
                            {truncatedIds[questionIndex] ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip className="custom-tooltip">
                                  <span dangerouslySetInnerHTML={{ __html: vocabName }}>
                                  </span></Tooltip>}
                              >
                                <div className="vocab-tile d-flex align-items-center gap-1" ref={measureTruncation(questionIndex)}>
                                  <div className="vocab-tile-index">{questionIndex + 1}.</div>
                                  <div className="vocab-tile-label" dangerouslySetInnerHTML={{ __html: vocabName }} />
                                </div>
                              </OverlayTrigger>
                            ) : (
                              <div className="vocab-tile d-flex align-items-center gap-1" ref={measureTruncation(questionIndex)}>
                                <div className="vocab-tile-index">{questionIndex + 1}.</div>
                                <div className="vocab-tile-label" dangerouslySetInnerHTML={{ __html: vocabName }} />
                              </div>
                            )}
                          </li>
                        )}
                      </>
                    );
                  })
                )}
              </ul>
              {!isPreview && <>
                <div className="mt-3">
                  <span>Drag and drop to reorder vocabulary terms</span>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        To change the position of a vocabulary term use the Drag and Drop feature.
                        <br /><br />
                        To view more vocabulary tiles drag over to Next &amp; Previous Buttons.
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon className="circle-question mx-1" icon={faCircleQuestion} />
                  </OverlayTrigger>
                </div>
                <div className="w-100">
                  <button
                    className={`btn btn-castleWhite mr-2 mt-3 w-30 ${(!canEdit || questionCount >= 100) ? 'disabled' : ''}`}
                    onClick={() =>
                      navigate(`/activitiesSearch/activities/${assignmentId}/castle_activities/vocabularies`)
                    }
                  >
                    <FontAwesomeIcon icon={faPlus} className="px-1" />
                  Add
                  </button>

                  <button
                    type="button"
                    disabled={questionCount <= 1}
                    className={`btn btn-castleWhite mr-2 mt-3 w-30 ${questionCount <= 1 ? 'disabled' : ''}`}
                    onClick={() => questionCount > 1 && onDeleteClick()}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} className="px-1" />
                  Remove
                  </button>

                  <button
                    type="button"
                    disabled={!canEdit || questionCount <= 1}
                    className={`btn btn-castleWhite mt-3 w-33 ${(!canEdit || questionCount <= 1) ? 'disabled' : ''}`}
                    onClick={() => (canEdit && questionCount > 1) && onShuffleClick()}
                  >
                    <FontAwesomeIcon icon={faShuffle} className="px-1" />
                  Shuffle
                  </button>
                </div>
              </>
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

VocabularyTerms.propTypes = {
  questions: PropTypes.object,
  assignedStatus: PropTypes.number,
  questionCount: PropTypes.number.isRequired,
  selectedQuestionIndex: PropTypes.number,
  pageQuestionIds: PropTypes.array.isRequired,
  numPages: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  startIndex: PropTypes.number.isRequired,
  endIndex: PropTypes.number.isRequired,
  isDragging: PropTypes.bool,
  dropIndex: PropTypes.number,
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onGetQuestionDetails: PropTypes.func.isRequired,
  onPrevPage: PropTypes.func.isRequired,
  onNextPage: PropTypes.func.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragOver: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onShuffleClick: PropTypes.func.isRequired,
  isPreview: PropTypes.bool.isRequired
};

export default VocabularyTerms;